import React, { useState, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { TabProvider } from '@tecuity/granite-ui/TabProvider'
import { TabPanel } from '@tecuity/granite-ui/TabPanel'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import { useRequestData } from '../../../Utilities/hooks'
import { SET_TRADEMARK_HISTORY, SET_RECORD_DETAILS } from '../RecordDetailsReducer'
import { editNote, deleteNote, saveNewNote, getEntityNotes } from '../RecordDetailsActions'
import LobbyistRepresentedEntitiesPanel from './LobbyistRepresentedEntitiesPanel'
import NotesPanel from '../NotesPanel'
import RecordHistory from 'components/RecordHistory/RecordHistory'
import { Body } from 'components/Shell/Shell'
import { Spinner } from 'components/Loaders/Loaders'
import GenericDetails from '../GenericDetails'
import * as api from '../../../requests'
import RecordDetailPageBackButton from '../RecordDetailPageBackButton'
import { FilingSourceTypeIds } from '../../../Utilities/Utilities'
import LobbyistExpendituresPanel from './LobbyistExpendituresPanel'

const LobbyistRecordDetails = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const route = 'LobbyistRecordDetails'
  const { recordDetails, recordAmendmentHistory, recordHistoryList } = useSelector(
    (state) => state.record,
    shallowEqual,
  )
  const RecordDetailsSettings = useRequestData(
    (cb, ecb) => api.GET_RECORD_DETAIL_SETTING_BY_TYPE(route, cb, ecb),
    [],
    {},
  )
  const [activeTabId, setActiveTabId] = useState('tab-1')
  const [loading, setLoading] = useState(false)

  const lobbyistNum = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location.pathname])

  useEffect(() => {
    setLoading(true)
    api.FETCH_RECORD_DETAILS_BY_ROUTE(
      route,
      lobbyistNum,
      (record) => {
        dispatch({ type: SET_RECORD_DETAILS, record: record })
        dispatch({ type: SET_TRADEMARK_HISTORY, history: record.history })
        setLoading(false)
      },
      (err) => {
        console.error(err)
        setLoading(false)
      },
    )
  }, [lobbyistNum])

  useEffect(() => {
    if (lobbyistNum)
      dispatch(getEntityNotes(FilingSourceTypeIds.Lobbyist, lobbyistNum))
  }, [lobbyistNum])

  const RepresentedEntitiesTitle = useMemo(() => {
    const { Tabs } = RecordDetailsSettings
    if (Array.isArray(Tabs) && Tabs.length > 2) return Tabs[2].Title
    return 'Represented Entities'
  }, [RecordDetailsSettings])

  const ExpendituresTitle = useMemo(() => {
    const { Tabs } = RecordDetailsSettings
    if (Array.isArray(Tabs) && Tabs.length > 3) return Tabs[3].Title
    return 'Expenditures'
  }, [RecordDetailsSettings])

  return (
    <TabProvider activeTabId={activeTabId} onChange={(id) => setActiveTabId(id)}>
      <Body className="trademark-record-details">
        <div className="row header-row">
          <h1>{RecordDetailsSettings.Title || 'Lobbyist License Details'}</h1>
          <div className="tablist" role="tablist">
            <TabButton id="tab-1" forPanelId="panel-1" label="History" />
            <TabButton
              id="tab-2"
              forPanelId="panel-2"
              label="Notes"
              // customCss={
              //   recordDetails['SeeNotesFlag'] === true
              //     ? { backgroundColor: 'red !important', color: '#fff !important' }
              //     : {}
              // }
            />
            <TabButton id="tab-3" forPanelId="panel-3" label={RepresentedEntitiesTitle} />
            <TabButton id="tab-4" forPanelId="panel-4" label={ExpendituresTitle} />
          </div>
        </div>
        {loading && (
          <div style={{ height: '100%', width: '100%', textAlign: 'center', padding: '4rem' }}>
            <Spinner className="page-loader" themeColor="dark-neutral" />
          </div>
        )}

        {!loading && <GenericDetails record={recordDetails} config={RecordDetailsSettings || {}} />}

        <div>
          {!loading && (
            <TabPanel tabId="tab-1" id="panel-1">
              <RecordHistory
                history={{
                  amendments: recordAmendmentHistory,
                  history: recordHistoryList,
                  template: !!RecordDetailsSettings.HistoryConfig
                    ? RecordDetailsSettings.HistoryConfig.filter((t) => t.IsEnabled)
                    : [],
                }}
                requiredVoidRight="LobbyistVoid"
                module="lobbyist"
              />
            </TabPanel>
          )}
          <TabPanel tabId="tab-2" id="panel-2">
            <NotesPanel
              saveNote={(noteText) => {
                dispatch(saveNewNote(noteText, FilingSourceTypeIds.Lobbyist, lobbyistNum))
              }}
              editNote={(note) => {
                dispatch(editNote(note, FilingSourceTypeIds.Lobbyist, lobbyistNum))
              }}
              deleteNote={(noteId) => {
                dispatch(deleteNote(noteId, FilingSourceTypeIds.Lobbyist, lobbyistNum))
              }}
            />
          </TabPanel>
          <TabPanel tabId="tab-3" id="panel-3">
            <LobbyistRepresentedEntitiesPanel config={RecordDetailsSettings} />
          </TabPanel>
          <TabPanel tabId="tab-4" id="panel-4">
            <LobbyistExpendituresPanel config={RecordDetailsSettings} />
          </TabPanel>
        </div>
        <RecordDetailPageBackButton />
      </Body>
    </TabProvider>
  )
}

export default LobbyistRecordDetails
